/**
 * MUSIC — two generated beds and three stings.
 *
 *  • lobby  — 90s of Fimbulwinter ambient: wind over a low drone, far lyre,
 *             a rare distant drum. Generated (Sonilo Music via Higgsfield).
 *  • fight  — 75s of war drums, low chant and horns. A BED, not a soundtrack:
 *             it sits under the combat SFX for a long bout.
 *  • stings — victory / defeat / fled. Each ducks the bed while it plays and
 *             hands the volume back afterwards.
 *
 * Loops have no authored seam, so one is made at runtime: the tail's last
 * seconds are overlap-added onto the head (equal-power), and the source loops
 * back before the mixed region ends — the join is mathematically continuous.
 *
 * Autoplay policy: the graph starts on the first user gesture after the shell
 * mounts (armOnFirstGesture). The preference persists in localStorage and is
 * honoured across every screen because the shell (HeroScreen) never unmounts.
 */

import lobbyUrl from '../assets/fimbulwake/audio/music-lobby.mp3';
import fightUrl from '../assets/fimbulwake/audio/music-fight.mp3';
import victoryUrl from '../assets/fimbulwake/audio/sting-victory.mp3';
import defeatUrl from '../assets/fimbulwake/audio/sting-defeat.mp3';
import fledUrl from '../assets/fimbulwake/audio/sting-fled.mp3';

const STORE_KEY = 'fw.music';

/** Which bed is playing. */
export type Scene = 'lobby' | 'fight';

const BED_URL: Record<Scene, string> = { lobby: lobbyUrl, fight: fightUrl };
/** Per-scene bed level: the war drums sit noticeably ABOVE the ambient wind,
 *  so walking into a bout is heard as a change, not suspected (CU-86cb58yuh
 *  item 8: "the music switch wasn't noticeable"). */
const BED_LEVELS: Record<Scene, number> = { lobby: 0.4, fight: 0.62 };
const bedLevel = () => BED_LEVELS[scene];
const LOOP_XFADE = 2.5; // seconds of tail folded onto the head

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let bedGain: GainNode | null = null;      // the bed only — stings bypass it, so ducking is one node
let bedSrc: AudioBufferSourceNode | null = null;
let bedToken = 0;                          // only the latest requested bed may start (loads are async)
let running = false;
let enabled = readPref();
let armed = false;
let scene: Scene = 'lobby';

function readPref(): boolean {
  try { return localStorage.getItem(STORE_KEY) !== 'off'; } catch { return true; }
}

export function isEnabled(): boolean { return enabled; }

/**
 * Has the player ever actually chosen? `enabled` defaults to ON, which is right
 * once someone is inside the game — but the LANDING is a page strangers arrive
 * at, and starting a music bed at the first click of a first visit is the kind
 * of thing people close a tab over. The landing therefore plays only on an
 * explicit yes; in-game behaviour is unchanged.
 */
export function hasExplicitPreference(): boolean {
  try { return localStorage.getItem(STORE_KEY) !== null; } catch { return false; }
}
export function currentScene(): Scene { return scene; }

export function setEnabled(on: boolean) {
  enabled = on;
  try { localStorage.setItem(STORE_KEY, on ? 'on' : 'off'); } catch { /* private mode */ }
  if (on) start(); else stop();
}

/** Start on the first pointer/key gesture (browsers refuse audio before one). */
export function armOnFirstGesture() {
  if (armed) return;
  armed = true;
  const kick = () => {
    window.removeEventListener('pointerdown', kick);
    window.removeEventListener('keydown', kick);
    if (enabled) start();
  };
  window.addEventListener('pointerdown', kick, { passive: true });
  window.addEventListener('keydown', kick, { passive: true });
}

function ac(): AudioContext | null {
  try {
    ctx ??= new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  } catch { return null; }
}

// ── buffers ─────────────────────────────────────────────────────────────────
const rawCache = new Map<string, Promise<AudioBuffer | null>>();
const loopCache = new Map<string, { buffer: AudioBuffer; loopEnd: number }>();

function loadBuffer(c: AudioContext, url: string): Promise<AudioBuffer | null> {
  let p = rawCache.get(url);
  if (!p) {
    p = fetch(url)
      .then((r) => r.arrayBuffer())
      .then((b) => c.decodeAudioData(b))
      .catch(() => null);
    rawCache.set(url, p);
  }
  return p;
}

/**
 * Make a buffer loop without a click: mix the last `fade` seconds onto the
 * first `fade` seconds with an equal-power crossfade, then loop the source at
 * `duration - fade`. At the jump the mix starts as pure tail, so the join is
 * continuous by construction.
 */
function makeLoop(c: AudioContext, url: string, buf: AudioBuffer): { buffer: AudioBuffer; loopEnd: number } {
  const hit = loopCache.get(url);
  if (hit) return hit;
  const fadeN = Math.min(Math.floor(LOOP_XFADE * buf.sampleRate), Math.floor(buf.length / 3));
  const out = c.createBuffer(buf.numberOfChannels, buf.length, buf.sampleRate);
  for (let ch = 0; ch < buf.numberOfChannels; ch++) {
    const d = buf.getChannelData(ch);
    const o = out.getChannelData(ch);
    o.set(d);
    const tail = buf.length - fadeN;
    for (let i = 0; i < fadeN; i++) {
      const t = i / fadeN;
      o[i] = d[i] * Math.sqrt(t) + d[tail + i] * Math.sqrt(1 - t);
    }
  }
  const made = { buffer: out, loopEnd: (buf.length - fadeN) / buf.sampleRate };
  loopCache.set(url, made);
  return made;
}

// ── scene machinery ─────────────────────────────────────────────────────────
function stopBedSource(src: AudioBufferSourceNode | null, afterMs: number) {
  if (!src) return;
  window.setTimeout(() => { try { src.stop(); } catch { /* already stopped */ } }, afterMs);
}

async function playBed(c: AudioContext, into: GainNode, s: Scene) {
  const token = ++bedToken;
  const raw = await loadBuffer(c, BED_URL[s]);
  if (!raw || token !== bedToken || !running) return;
  const { buffer, loopEnd } = makeLoop(c, BED_URL[s], raw);
  const src = c.createBufferSource();
  src.buffer = buffer;
  src.loop = true;
  src.loopStart = 0;
  src.loopEnd = loopEnd;
  src.connect(into);
  src.start();
  bedSrc = src;
}

export function start() {
  if (running || !enabled) return;
  const c = ac(); if (!c) return;
  running = true;

  master = c.createGain();
  master.gain.value = 1;
  master.connect(c.destination);

  bedGain = c.createGain();
  bedGain.gain.setValueAtTime(0.0001, c.currentTime);
  bedGain.gain.exponentialRampToValueAtTime(bedLevel(), c.currentTime + 3); // slow fade-in
  bedGain.connect(master);

  void playBed(c, bedGain, scene);

  // warm the rest of the set while the hold is quiet — a fight must not open
  // on a silent bed, and a victory must not wait on the network
  window.setTimeout(() => {
    if (!running) return;
    [BED_URL.fight, victoryUrl, defeatUrl, fledUrl].forEach((u) => void loadBuffer(c, u));
  }, 2500);
}

/**
 * Swap beds with a crossfade. Cheap to call with the scene already running —
 * screens call it on mount, so it must be idempotent.
 */
export function setScene(s: Scene) {
  if (scene === s) return;
  scene = s;
  const c = ctx;
  if (!running || !c || !bedGain || !master) return;

  const t = c.currentTime;
  const FADE = 1.1;
  const old = bedGain;
  const oldSrc = bedSrc;
  bedSrc = null;
  old.gain.cancelScheduledValues(t);
  old.gain.setTargetAtTime(0.0001, t, FADE / 3);
  stopBedSource(oldSrc, (FADE + 0.5) * 1000);

  const fresh = c.createGain();
  fresh.gain.setValueAtTime(0.0001, t);
  fresh.gain.setTargetAtTime(BED_LEVELS[s], t + FADE * 0.5, FADE / 3);
  fresh.connect(master);
  bedGain = fresh;
  void playBed(c, fresh, s);

  window.setTimeout(() => { try { old.disconnect(); } catch { /* detached */ } }, (FADE + 0.6) * 1000);
}

export function stop() {
  if (!running) return;
  running = false;
  bedToken++; // orphan any bed still loading
  const c = ctx;
  const m = master;
  const b = bedGain;
  const src = bedSrc;
  master = null;
  bedGain = null;
  bedSrc = null;
  if (c && m) {
    (b ?? m).gain.setTargetAtTime(0.0001, c.currentTime, 0.4);
    stopBedSource(src, 1600);
    window.setTimeout(() => { try { m.disconnect(); } catch { /* detached */ } }, 1600);
  } else {
    stopBedSource(src, 0);
  }
}

// ── stings ──────────────────────────────────────────────────────────────────
/** Duck the bed under a sting and hand the level back when it is done. */
function duck(c: AudioContext, seconds: number) {
  if (!bedGain) return;
  const t = c.currentTime;
  bedGain.gain.cancelScheduledValues(t);
  bedGain.gain.setTargetAtTime(bedLevel() * 0.18, t, 0.12);
  bedGain.gain.setTargetAtTime(bedLevel(), t + seconds, 0.6);
}

/** Stings ride the master so they are never swallowed by a ducked bed.
 *  Music off means silence — stings are music too. */
function playSting(url: string, level: number) {
  if (!enabled) return;
  const c = ac(); if (!c) return;
  void loadBuffer(c, url).then((buf) => {
    if (!buf || !enabled) return;
    duck(c, Math.min(buf.duration, 8));
    const src = c.createBufferSource();
    src.buffer = buf;
    const g = c.createGain();
    g.gain.value = level;
    src.connect(g).connect(master ?? c.destination);
    src.start();
  });
}

/** VICTORY — horns rise over the drums. */
export function victory() { playSting(victoryUrl, 0.8); }

/** DEFEAT — the call falls, and the wind takes the rest. */
export function defeat() { playSting(defeatUrl, 0.8); }

/** WALKED OUT — no defeat, no triumph: a heartbeat and a gust. */
export function fled() { playSting(fledUrl, 0.7); }
